import React, { createContext, useContext, useState, useEffect } from "react";
import { parseMoney } from "../utils/money";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeUserBalances = (userData = {}) => ({
    ...userData,
    affordable_data_balance: parseMoney(userData.affordable_data_balance),
    airtime_data_balance: parseMoney(userData.airtime_data_balance),
    commission_rate: parseMoney(userData.commission_rate),
  });

  // Load tokens from localStorage on app start
  useEffect(() => {
    const storedAccessToken = localStorage.getItem("access_token");
    const storedRefreshToken = localStorage.getItem("refresh_token");
    const storedUser = localStorage.getItem("user");

    if (storedAccessToken && storedRefreshToken) {
      setAccessToken(storedAccessToken);
      setRefreshToken(storedRefreshToken);
      if (storedUser) {
        setUser(normalizeUserBalances(JSON.parse(storedUser)));
      }
    }
    setLoading(false);
  }, []);

  const login = (tokens, userData) => {
    const normalizedUser = normalizeUserBalances(userData);
    setAccessToken(tokens.access_token);
    setRefreshToken(tokens.refresh_token);
    setUser(normalizedUser);
    localStorage.setItem("access_token", tokens.access_token);
    localStorage.setItem("refresh_token", tokens.refresh_token);
    localStorage.setItem("user", JSON.stringify(normalizedUser));
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
  };

  // 🔥 NEW: Update user balance method
  const updateUserBalance = (balanceData) => {
    if (user) {
      const updatedUser = {
        ...user,
        affordable_data_balance: parseMoney(balanceData.affordable_data_balance),
        airtime_data_balance: parseMoney(balanceData.airtime_data_balance),
        commission_rate: parseMoney(balanceData.commission_rate),
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    }
  };

  const isAuthenticated = () => {
    return accessToken && user;
  };

  const value = {
    user,
    accessToken,
    refreshToken,
    login,
    logout,
    updateUserBalance, // 🔥 Add this to the context value
    isAuthenticated,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
